'use client';

import { c as _c } from "react/compiler-runtime";
import { useRef, useState, useEffect } from 'react';
import Image from 'next/image';
import { useParams } from 'next/navigation';
import { cn } from '@/hooks/utils';
import gsap from 'gsap';
import { SplitText } from 'gsap/SplitText';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import dynamic from 'next/dynamic';
const CPPNBackground = dynamic(() => import('@/components/ui/CPPNBackground'), {
  ssr: false,
  loading: () => <div className="absolute inset-0 -z-20 h-full w-full bg-inherit" />
});
import { Button } from '@/components/ui/Button';
import { ImageComponent } from '@/components/ui/ImageComponent';
gsap.registerPlugin(SplitText, ScrollTrigger);
if (typeof window !== 'undefined') {
  gsap.ticker.lagSmoothing(1000, 16);
}
import { useThemeSync } from '@/hooks/useThemeSync';
import { heroData } from '../_data/homepage';
import { ASSETS_URL } from '@/constants';

// ... (imports)

// ===================== HERO =====================
interface HeroProps {
  dict: any;
  title?: React.ReactNode;
  description?: string;
  badgeText?: string;
  ctaButtons?: Array<{
    text: string;
    href: string;
    primary?: boolean;
    icon?: React.ReactNode;
  }>;
  productImage?: string;
  isDark?: boolean;
}
export function Hero(t0) {
  const $ = _c(46);
  const {
    dict,
    title,
    description,
    badgeText,
    isDark: propIsDark
  } = t0;
  const {
    isDark
  } = useThemeSync(propIsDark);
  const params = useParams();
  const lang = params?.lang as string || "en";
  const sectionRef = useRef(null);
  const headerRef = useRef(null);
  const paraRef = useRef(null);
  const ctaRef = useRef(null);
  const badgeRef = useRef(null);
  const imageRef = useRef(null);
  const defaultCtaButtons = heroData.ctaButtons;
  const finalCtaButtons = defaultCtaButtons;
  const finalDescription = description || dict.hero.subtitle;
  const finalBadgeText = badgeText || heroData.badgeText;
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      if (!headerRef.current) {
        return;
      }
      const ctx = gsap.context(() => {
        requestAnimationFrame(() => {
          document.fonts.ready.then(() => {
            const split = new SplitText(headerRef.current, {
              type: "lines",
              wordsClass: "lines"
            });
            gsap.set(split.lines, {
              filter: "blur(10px)",
              yPercent: 20,
              autoAlpha: 0,
              scale: 1.02,
              transformOrigin: "50% 100%"
            });
            if (badgeRef.current) {
              gsap.set(badgeRef.current, {
                autoAlpha: 0,
                y: -8
              });
            }
            if (paraRef.current) {
              gsap.set(paraRef.current, {
                autoAlpha: 0,
                y: 8
              });
            }
            if (ctaRef.current) {
              gsap.set(ctaRef.current, {
                autoAlpha: 0,
                y: 8
              });
            }
            const tl = gsap.timeline({
              defaults: {
                ease: "power3.out"
              }
            });
            if (badgeRef.current) {
              tl.to(badgeRef.current, {
                autoAlpha: 1,
                y: 0,
                duration: 0.4
              }, 0);
            }
            tl.to(split.lines, {
              filter: "blur(0px)",
              yPercent: 0,
              autoAlpha: 1,
              scale: 1,
              duration: 0.6,
              stagger: 0.08
            }, 0.05);
            if (paraRef.current) {
              tl.to(paraRef.current, {
                autoAlpha: 1,
                y: 0,
                duration: 0.4
              }, "-=0.4");
            }
            if (ctaRef.current) {
              tl.to(ctaRef.current, {
                autoAlpha: 1,
                y: 0,
                duration: 0.4
              }, "-=0.3");
            }
          });
        });
      }, sectionRef);
      return () => ctx.revert();
    };
    t2 = [];
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  useEffect(t1, t2);
  const t3 = isDark ? "dark" : "light";
  let t4;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = cn("relative flex min-h-[100svh] w-full flex-col items-center overflow-hidden");
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  let t5;
  if ($[3] !== isDark) {
    t5 = <CPPNBackground flip={true} isDark={isDark} />;
    $[3] = isDark;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const t6 = isDark ? "border-white/20 bg-white/10" : "border-slate-200 bg-[rgba(255,255,255,0.10)]";
  let t7;
  if ($[5] !== t6) {
    t7 = cn("mb-6 inline-flex items-center gap-2 rounded-[24px] border px-4 py-2 backdrop-blur-md mt-auto", t6);
    $[5] = t6;
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  const t8 = isDark ? "text-white" : "text-[#0B0623]";
  let t9;
  if ($[7] !== t8) {
    t9 = cn("font-sans text-sm font-medium tracking-tight", t8);
    $[7] = t8;
    $[8] = t9;
  } else {
    t9 = $[8];
  }
  let t10;
  if ($[9] !== finalBadgeText || $[10] !== t9) {
    t10 = <span className={t9}>{finalBadgeText}</span>;
    $[9] = finalBadgeText;
    $[10] = t9;
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  let t11;
  if ($[12] !== t10 || $[13] !== t7) {
    t11 = <div ref={badgeRef} className={t7}>{t10}</div>;
    $[12] = t10;
    $[13] = t7;
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  const t12 = isDark ? "text-white" : "text-[#0B0623]";
  let t13;
  if ($[15] !== t12) {
    t13 = cn("font-bricolage mb-3 sm:mb-4 text-center text-[32px] leading-[1.1] font-bold tracking-tight sm:text-7xl md:text-[80px]", t12);
    $[15] = t12;
    $[16] = t13;
  } else {
    t13 = $[16];
  }
  let t14;
  if ($[17] !== dict || $[18] !== title) {
    t14 = title || <>{dict.hero.title.split(" ").slice(0, -3).join(" ")}<br /><span className="bg-linear-to-r from-[#573DF7] to-[#E470FF] bg-clip-text text-transparent">{dict.hero.title.split(" ").slice(-3).join(" ")}</span></>;
    $[17] = dict;
    $[18] = title;
    $[19] = t14;
  } else {
    t14 = $[19];
  }
  let t15;
  if ($[20] !== t13 || $[21] !== t14) {
    t15 = <h1 ref={headerRef} className={t13}>{t14}</h1>;
    $[20] = t13;
    $[21] = t14;
    $[22] = t15;
  } else {
    t15 = $[22];
  }
  const t16 = isDark ? "text-white" : "text-[#302B44]";
  let t17;
  if ($[23] !== t16) {
    t17 = cn("mb-6 sm:mb-8 max-w-2xl text-center font-sans text-base leading-relaxed font-medium tracking-tight sm:px-12 sm:text-lg", t16);
    $[23] = t16;
    $[24] = t17;
  } else {
    t17 = $[24];
  }
  let t18;
  if ($[25] !== finalDescription || $[26] !== t17) {
    t18 = <p ref={paraRef} className={t17}>{finalDescription}</p>;
    $[25] = finalDescription;
    $[26] = t17;
    $[27] = t18;
  } else {
    t18 = $[27];
  }
  let t19;
  if ($[28] !== isDark || $[29] !== lang) {
    t19 = finalCtaButtons.map((button, index) => {
      const formattedHref = button.href?.startsWith("/") ? `/${lang}${button.href}` : button.href;
      return <Button key={index} href={formattedHref} target={button.primary ? "_blank" : undefined} rel={button.primary ? "noopener noreferrer" : undefined} variant={button.primary ? "primary" : "outline"} className={cn("h-[48px] w-[240px] sm:w-auto gap-[10px] px-6 text-[16px]", !button.primary && (isDark ? "" : " text-[#0B0623] hover:bg-[#0B0623]/5"))}>{button.text}{button.primary && <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M12.5511 4.4751V4.47695L11.4453 4.52489L12.5511 4.4751Z" fill="white" /><path d="M22 11.124V12.9258C19.3988 12.9258 17.5044 14.0026 16.3699 16.1256C15.4802 17.7904 15.3885 19.5081 15.3879 19.5249L13.6031 19.445C13.6067 19.3603 13.7044 17.3538 14.7644 15.3329C15.1848 14.5299 15.7929 13.6627 16.6606 12.9258H2V11.124H16.6606C15.7929 10.3865 15.1854 9.51929 14.7644 8.71692C13.7044 6.69538 13.6067 4.68946 13.6031 4.60477L15.3879 4.5249C15.3879 4.54171 15.4802 6.25936 16.3699 7.92416C17.5044 10.0472 19.3988 11.124 22 11.124Z" fill="white" /></svg>}</Button>;
    });
    $[28] = isDark;
    $[29] = lang;
    $[30] = t19;
  } else {
    t19 = $[30];
  }
  let t20;
  if ($[31] !== t19) {
    t20 = <div ref={ctaRef} className="flex w-full flex-col sm:w-auto sm:flex-row items-center justify-center gap-3">{t19}</div>;
    $[31] = t19;
    $[32] = t20;
  } else {
    t20 = $[32];
  }
  let t21;
  if ($[33] !== t11 || $[34] !== t15 || $[35] !== t18 || $[36] !== t20) {
    t21 = <div className="flex flex-1 flex-col items-center justify-center w-full">{t11}{t15}{t18}{t20}</div>;
    $[33] = t11;
    $[34] = t15;
    $[35] = t18;
    $[36] = t20;
    $[37] = t21;
  } else {
    t21 = $[37];
  }
  let t22;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <div className="absolute -inset-4 rounded-3xl bg-purple-400/10 blur-3xl" />;
    $[38] = t22;
  } else {
    t22 = $[38];
  }
  let t23;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <div ref={imageRef} className="relative mt-10 md:mt-16 sm:mt-16 w-full max-w-[1086px] flex flex-col justify-end">{t22}<div className="relative overflow-hidden rounded-t-2xl sm:rounded-2xl shrink-0 translate-y-1 sm:translate-y-0"><ImageComponent src={heroData.productImage} alt="Product dashboard preview" width={1920} height={1080} className="h-auto w-full object-cover object-top" disableAnimation={true} preload={true} /></div></div>;
    $[39] = t23;
  } else {
    t23 = $[39];
  }
  let t24;
  if ($[40] !== t21) {
    t24 = <div className="relative mx-auto flex w-full max-w-[1240px] flex-1 flex-col items-center px-6 pt-24 sm:pt-32 text-center pb-0">{t21}{t23}</div>;
    $[40] = t21;
    $[41] = t24;
  } else {
    t24 = $[41];
  }
  let t25;
  if ($[42] !== t24 || $[43] !== t3 || $[44] !== t5) {
    t25 = <section ref={sectionRef} data-theme={t3} suppressHydrationWarning={true} className={t4}>{t5}{t24}</section>;
    $[42] = t24;
    $[43] = t3;
    $[44] = t5;
    $[45] = t25;
  } else {
    t25 = $[45];
  }
  return t25;
}